<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <div ref="smodalWrapper">
    <!--eslint-disable-next-line vue/no-restricted-html-elements-->
    <b-modal
      v-bind="$attrs"
      :id="modalId"
      ref="smodal"
      :visible="displayModal"
      :data-test="$attrs['data-test'] || 's-confirm-modal'"
      hide-header-close
      :title="title"
      :cancel-title="cancelTitle || $t('global.cancel')"
      :ok-title="okTitle || $t('global.save')"
      :hide-header="false"
      :hide-footer="false"
      dialog-class="modal--confirm"
      :body-class="bodyClasses"
      cancel-variant="link"
      title-tag="h1"
      scrollable
      centered
      :return-focus="undefined"
      @shown="onModalShown"
      v-on="$listeners"
      @change="emitChange"
    >
      <template #default>
        <slot name="default" />

        <!-- DESCRIPTION -->
        <slot v-if="$hasSlotContent($slots['description'])" name="description" />
        <div v-else-if="!!descriptionPortalTarget" data-test="s-confirm-modal-description-portal-target">
          <portal-target :name="descriptionPortalId" />
        </div>
        <p v-else-if="description"
           class="m-0"
           data-test="s-confirm-modal-description"
        >{{ description }}</p>

        <!-- OPTIONAL TOGGLE -->
        <SListItem
          v-if="hasToggle"
          data-test="s-confirm-modal-toggle"
          class="mt-4"
          :title="toggleDisplayLabel"
          :aria-pressed="localToggle ? 'true' : 'false'"
          @click="localToggle = !localToggle"
        >
          <template #right-action>
            <SCheckbox
              v-model="localToggle"
              data-test="s-confirm-modal-toggle-checkbox"
              name="confirm-toggle"
              :label-text="toggleDisplayLabel"
              checkbox-type="switch"
            />
          </template>
        </SListItem>
      </template>

      <template v-if="!okTitle && !cancelTitle" #modal-footer>
        <div v-if="footerPortalTarget"
             :class="footerPortalClass"
             data-test="s-confirm-modal-footer-portal-target"
        >
          <portal-target :name="footerPortalId" />
        </div>
        <slot v-else name="modal-footer"></slot>
      </template>
    </b-modal>
  </div>
</template>

<script>
import SCheckbox from 'src/components/base/SCheckbox.vue'
import modalFocusMixin from 'src/mixins/miscellaneous/modalFocusMixin.js'
import { PortalTarget } from 'portal-vue'
import { getWormholeContent } from '@/components/base/SModal/portalHelper.js'

export default {
  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  inheritAttrs: false,

  name: 'SConfirmModal',

  components: { PortalTarget, SCheckbox },

  mixins: [modalFocusMixin],

  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      required: true,
    },

    description: {
      type: String,
      default: null,
    },

    okTitle: {
      type: String,
      default: null,
    },

    cancelTitle: {
      type: String,
      default: null,
    },

    hasToggle: {
      type: Boolean,
      default: false,
    },

    localStorageToggleName: {
      type: String,
      default: null,
    },

    toggleDisplayLabel: {
      type: String,
      default: null,
    },

    // Element which should be focused when the modal is closed
    // Useful if modal was opened by dropdown or some async action was executed before opening the modal
    returnFocus: {
      type: [String, HTMLElement],
      default: undefined,
    },

    // When a SModal is used to get input from the user (e.g. submitting a sicknote), we "sometimes" ask the user
    // to confirm that they are going to lose changes. The SConfirmModal would by default attempt to return
    // the focus to trigger element of the SModal. But in this use-case, it's going to be closed as well.
    // This prop disables the focus return to the trigger element.
    preventFocusReturnOnModalClose: {
      type: Boolean,
      default: false,
    },

    footerPortalClass: {
      type: String,
      default: 'w-100',
    },
  },

  data: () => ({
    modalId: null,
    portalId: null,
    displayModal: false,
    isScreenReaderActive: false,
    isIOS: false,
    isAndroid: false,
    localToggle: false,
  }),

  computed: {
    descriptionPortalId() {
      return `description-${this.portalId}`
    },

    descriptionPortalTarget() {
      return getWormholeContent(this.descriptionPortalId)?.length
    },

    footerPortalId() {
      return `footer-${this.portalId}`
    },

    footerPortalTarget() {
      return getWormholeContent(this.footerPortalId)?.length
    },

    bodyClasses() {
      const bodyClasses = []
      bodyClasses.push(this.$attrs['body-class'])
      if (!this.hasContent) bodyClasses.push('d-none')
      return bodyClasses
    },

    hasContent() {
      return this.description || this.$hasSlotContent(this.$slots.description) || this.descriptionPortalTarget || this.hasToggle
    },
  },

  emits: ['update:modelValue', 'change', 'close'],

  methods: {
    emitChange(changeValue) {
      this.$emit('update:modelValue', changeValue)
      this.$emit('change', changeValue)
      if (!changeValue) this.$emit('close')
    },

    onModalShown() {
      this.overwriteA11yAttributes()
      this.makeTitleFocusable()
      this.setInitialFocusElement(true)
    },

    overwriteA11yAttributes() {
      // alertdialog: Bootstrap's modals have the wrong role when used as alert/confirmation dialog
      this.$refs?.smodal?.$refs?.modal?.setAttribute('role', 'alertdialog')

      // aria-describedby: Bootstrap's modals automatically set aria-describedby attribute to the modal content element.
      // This causes the whole content to be read out when navigating to the title.
      this.$refs?.smodal?.$refs?.modal?.removeAttribute('aria-describedby')
    },

    makeTitleFocusable() {
      // Screenreaders need to set their first focus to the title of the modal. It needs attribute tabindex="-1"
      // to be (programmatically) focusable.
      if (!this.isScreenReaderActive) return
      const titleElement = this.$refs?.smodal?.$refs?.modal?.querySelector('.modal-title')
      if (!titleElement) return
      titleElement.setAttribute('tabindex', '-1')
    },
  },

  watch: {
    modelValue: {
      async handler(displayModal, oldDisplayModal) {
        this.displayModal = displayModal

        // modal opens, remember triggering element and add pseudo element for iOS
        if (displayModal && !oldDisplayModal) {
          await this.$nextTick()
          await this.onModalDisplayed(this.returnFocus || this.$refs?.smodal?.$_returnFocus)

          // Stop Bootstrap from returning focus to the element that opened the modal. We handle that ourselves.
          if (this.$refs?.smodal?.$_returnFocus) {
            this.$refs.smodal.$_returnFocus = undefined
          }
        }

        // modal closes, return the focus to the triggering element that opened the modal
        if (!displayModal && oldDisplayModal) {
          await this.onModalHidden(this.preventFocusReturnOnModalClose)
        }
      },

      immediate: true,
    },

    refocusFirstElement: {
      async handler(newVal, oldVal) {
        if (oldVal === newVal || !this.displayModal) return
        await this.$nextTick()
        await this.setInitialFocusElement(true, true)
      },
    },

    localToggle(val) {
      if (!this.localStorageToggleName || !localStorage) return
      localStorage.setItem(this.localStorageToggleName, val)
    },

    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
    },
  },

  beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
  },

  async mounted() {
    if (import.meta.env.IS_STORYBOOK) return
    this.isIOS = this.$store.state.isIOS
    this.isAndroid = this.$store.state.isAndroid
  },

  created() {
    this.modalId = this.$uuid()
    this.portalId = this.$uuid()

    if (!this.localStorageToggleName || !localStorage) return
    this.localToggle = JSON.parse(localStorage.getItem(this.localStorageToggleName)) || false
  },
}
</script>

<!--
  Unscoped:
  The Modal is beeing attached to the body (DOM), so :deep() selector does not work anymore (it was working with Vue2).
  So we have to use unscoped styles for modals.
-->
<style lang="scss">
.modal--confirm .modal-header {
  padding: 1rem;
}

.modal--confirm .modal-title {
  text-align: left !important;
  letter-spacing: -0.2px;
  line-height: pxtorem(24) !important;
  /* stylelint-disable-next-line declaration-property-value-allowed-list */
  font-size: $h3-font-size;
  font-weight: $font-weight-bold;
}

.modal--confirm .modal-body {
  padding-top: 0 !important;
}

.modal--confirm .modal-footer {
  background-color: $color-secondary;
  border-top: 1px solid $color-secondary;
}

.modal--confirm .btn-primary {
  min-width: 120px !important;
}
</style>
